/**
 * Doctor Search Provider
 * Searches doctors by name, RFC, email, and specialty
 * Shows expanded results with quick actions directly in search
 *
 * UI Design: Primary result (profile) is prominent, sub-actions are grouped below
 */

import { User, Plus, FileText, FolderOpen, Pencil, Phone, Mail } from 'lucide-react';
import { getDoctors } from '@/services/doctorsService';
import type { Doctor } from '@/features/doctors/types/doctor.types';
import type { SearchProvider, SearchResult, SearchAction } from '../types';

const MAX_RESULTS = 4;

// Extended data type that includes action info and styling hints
interface DoctorActionData {
  doctor: Doctor;
  actionType: 'profile' | 'new-task' | 'tasks' | 'documents' | 'edit';
  isPrimary: boolean; // For styling - primary results get special treatment
  isLastInGroup: boolean; // For styling - adds bottom margin after group
  iconColor?: string; // Tailwind color class for the icon
}

function createExpandedDoctorResults(
  doctor: Doctor,
  isLastDoctor: boolean
): SearchResult<DoctorActionData>[] {
  // Build keywords for fuzzy matching
  const baseKeywords = [doctor.fullName];
  if (doctor.rfc) baseKeywords.push(doctor.rfc);
  if (doctor.email) baseKeywords.push(doctor.email);
  if (doctor.phone) baseKeywords.push(doctor.phone);
  if (doctor.specialtyName) baseKeywords.push(doctor.specialtyName);

  // Build rich subtitle with full-width layout
  const subtitle = (
    <div className="flex w-full items-center justify-between gap-4">
      {/* Left: Specialty and RFC */}
      <span className="text-xs text-muted-foreground">
        {[doctor.specialtyName, doctor.rfc].filter(Boolean).join(' • ')}
      </span>

      {/* Right: Contact info */}
      <div className="flex shrink-0 items-center gap-4 text-xs text-foreground/60">
        {doctor.phone && (
          <span className="flex items-center gap-1">
            <Phone className="h-3 w-3" />
            {doctor.phone}
          </span>
        )}
        {doctor.email && (
          <span className="flex items-center gap-1">
            <Mail className="h-3 w-3" />
            {doctor.email}
          </span>
        )}
      </div>
    </div>
  );

  const results: SearchResult<DoctorActionData>[] = [
    // Primary: Go to profile - PROMINENT
    {
      id: `${doctor.id}-profile`,
      title: doctor.fullName,
      subtitle,
      icon: (
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100 dark:bg-teal-900/50">
          <User className="h-5 w-5 text-teal-600 dark:text-teal-400" />
        </div>
      ),
      keywords: [...baseKeywords, 'perfil', 'profile', 'ver'],
      data: { doctor, actionType: 'profile', isPrimary: true, isLastInGroup: false },
      providerId: 'doctors',
    },
    // Sub-action: New task
    {
      id: `${doctor.id}-new-task`,
      title: 'Nuevo trámite',
      subtitle: undefined,
      icon: <Plus className="h-3.5 w-3.5 text-emerald-500" />,
      keywords: [...baseKeywords, 'nuevo', 'tramite', 'crear', 'new', 'task'],
      data: {
        doctor,
        actionType: 'new-task',
        isPrimary: false,
        isLastInGroup: false,
        iconColor: 'text-emerald-500',
      },
      providerId: 'doctors',
    },
    // Sub-action: View tasks
    {
      id: `${doctor.id}-tasks`,
      title: 'Ver trámites',
      subtitle: undefined,
      icon: <FileText className="h-3.5 w-3.5 text-amber-500" />,
      keywords: [...baseKeywords, 'tramites', 'tasks', 'lista', 'ver'],
      data: {
        doctor,
        actionType: 'tasks',
        isPrimary: false,
        isLastInGroup: false,
        iconColor: 'text-amber-500',
      },
      providerId: 'doctors',
    },
    // Sub-action: View documents
    {
      id: `${doctor.id}-documents`,
      title: 'Documentos',
      subtitle: undefined,
      icon: <FolderOpen className="h-3.5 w-3.5 text-violet-500" />,
      keywords: [...baseKeywords, 'documentos', 'documents', 'archivos', 'ver'],
      data: {
        doctor,
        actionType: 'documents',
        isPrimary: false,
        isLastInGroup: false,
        iconColor: 'text-violet-500',
      },
      providerId: 'doctors',
    },
    // Sub-action: Edit - last in group
    {
      id: `${doctor.id}-edit`,
      title: 'Editar perfil',
      subtitle: undefined,
      icon: <Pencil className="h-3.5 w-3.5 text-slate-400" />,
      keywords: [...baseKeywords, 'editar', 'edit', 'modificar'],
      data: {
        doctor,
        actionType: 'edit',
        isPrimary: false,
        isLastInGroup: !isLastDoctor,
        iconColor: 'text-slate-400',
      },
      providerId: 'doctors',
    },
  ];

  return results;
}

export const DoctorSearchProvider: SearchProvider<DoctorActionData> = {
  id: 'doctors',
  name: 'Médicos',
  icon: <User className="h-4 w-4" />,
  priority: 1,
  placeholder: 'Buscar por nombre, RFC o email...',

  async search(query: string): Promise<SearchResult<DoctorActionData>[]> {
    if (!query || query.length < 2) {
      return [];
    }

    try {
      const response = await getDoctors({
        search: query,
        limit: MAX_RESULTS,
        isActive: true,
      });

      // Expand each doctor into multiple action results
      const expandedResults = response.data.flatMap((doctor, index) =>
        createExpandedDoctorResults(doctor, index === response.data.length - 1)
      );

      return expandedResults;
    } catch (error) {
      console.error('[DoctorSearchProvider] Search error:', error);
      return [];
    }
  },

  getActions(data: DoctorActionData): SearchAction[] {
    const { doctor, actionType } = data;

    const actionPaths: Record<typeof actionType, string> = {
      profile: `/doctors/${doctor.id}`,
      'new-task': `/doctors/${doctor.id}?tab=tasks&action=new`,
      tasks: `/doctors/${doctor.id}?tab=tasks`,
      documents: `/doctors/${doctor.id}?tab=documents`,
      edit: `/doctors/${doctor.id}/edit`,
    };

    return [
      {
        id: actionType,
        label: 'Ir',
        path: actionPaths[actionType],
      },
    ];
  },
};
